import React, { createContext, useContext, useState, useCallback } from 'react';
import { App } from 'antd';

interface ErrorContextType {
  showError: (message: string, description?: string) => void;
  showSuccess: (message: string, description?: string) => void;
  showInfo: (message: string, description?: string) => void;
  showWarning: (message: string, description?: string) => void;
  clearNotifications: () => void;
  isError: boolean;
  errorMessage: string | null;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: React.ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { notification } = App.useApp();

  const showError = useCallback((messageText: string, description?: string) => {
    setIsError(true);
    setErrorMessage(messageText);
    notification.error({
      message: messageText,
      description,
      placement: 'topRight',
      duration: 5,
      style: {
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  }, [notification]);

  const showSuccess = useCallback((messageText: string, description?: string) => {
    notification.success({
      message: messageText,
      description,
      placement: 'topRight',
      duration: 4,
      style: {
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  }, [notification]);

  const showInfo = useCallback((messageText: string, description?: string) => {
    notification.info({
      message: messageText,
      description,
      placement: 'topRight',
      duration: 4,
      style: {
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  }, [notification]);

  const showWarning = useCallback((messageText: string, description?: string) => {
    notification.warning({
      message: messageText,
      description,
      placement: 'topRight',
      duration: 4,
      style: {
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  }, [notification]);

  const clearNotifications = useCallback(() => {
    notification.destroy();
  }, [notification]);

  const clearError = useCallback(() => {
    setIsError(false);
    setErrorMessage(null);
  }, []);

  const value: ErrorContextType = {
    showError,
    showSuccess,
    showInfo,
    showWarning,
    clearNotifications,
    isError,
    errorMessage,
    clearError,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
}; 